export const ageBands = ["unknown", "under75", "over75"] as const;
export type AgeBand = (typeof ageBands)[number];

export const egfrBands = ["unknown", "gt40", "30to40", "20to30", "lt20"] as const;
export type EgfrBand = (typeof egfrBands)[number];

export const sedationStates = ["unknown", "sedated", "extubated"] as const;
export type SedationState = (typeof sedationStates)[number];

export const opioidChoices = ["unknown", "morphine", "oxycodone", "fentanyl"] as const;
export type OpioidChoice = (typeof opioidChoices)[number];

export const stageValues = [
  "unknown",
  "before-surgery",
  "during-surgery",
  "handover",
  "itu-sedated",
  "after-extubation",
  "breakthrough-pain",
  "escalation",
] as const;
export type StageValue = (typeof stageValues)[number];

export const supportedFlags = [
  "chronicPain",
  "chronicGabapentinoid",
  "polyneuropathy",
  "opioidUse",
  "pregabalinAllergy",
  "neuropathicPain",
  "methadone",
  "ivdu",
  "mentalHealth",
  "oralNotTolerated",
  "functionalLimitation",
  "bmiAbove30",
  "weightAbove50",
] as const;
export type WorkflowFlag = (typeof supportedFlags)[number];

export interface WorkflowState {
  age: AgeBand;
  egfr: EgfrBand;
  stage: StageValue;
  sedation: SedationState;
  pain: number | null;
  cpot: number | null;
  opioid: OpioidChoice;
  route: string | null;
  flags: WorkflowFlag[];
}

export const defaultWorkflowState: WorkflowState = {
  age: "unknown",
  egfr: "unknown",
  stage: "unknown",
  sedation: "unknown",
  pain: null,
  cpot: null,
  opioid: "unknown",
  route: null,
  flags: [],
};

export const urlStateKeys = ["age", "egfr", "stage", "sedation", "pain", "cpot", "opioid", "route", "flags"] as const;
export const forbiddenUrlStateKeys = ["name", "nhs", "mrn", "dob", "dateOfBirth", "notes", "note", "comment", "comments"];

function isOneOf<T extends readonly string[]>(value: string | null, allowed: T): value is T[number] {
  return value !== null && allowed.includes(value);
}

function parseScore(value: string | null, max: number) {
  if (value === null || value.trim() === "") {
    return null;
  }

  const score = Number(value);
  if (!Number.isInteger(score) || score < 0 || score > max) {
    return null;
  }

  return score;
}

function parseFlags(value: string | null): WorkflowFlag[] {
  if (!value) {
    return [];
  }

  const parsed = value
    .split(",")
    .map((flag) => flag.trim())
    .filter((flag): flag is WorkflowFlag => isOneOf(flag, supportedFlags));

  return Array.from(new Set(parsed)).sort();
}

export function parseWorkflowState(search: string | URLSearchParams): WorkflowState {
  const params = typeof search === "string" ? new URLSearchParams(search) : search;
  const age = params.get("age");
  const egfr = params.get("egfr");
  const stage = params.get("stage");
  const sedation = params.get("sedation");
  const opioid = params.get("opioid");
  const route = params.get("route");

  return {
    age: isOneOf(age, ageBands) ? age : defaultWorkflowState.age,
    egfr: isOneOf(egfr, egfrBands) ? egfr : defaultWorkflowState.egfr,
    stage: isOneOf(stage, stageValues) ? stage : defaultWorkflowState.stage,
    sedation: isOneOf(sedation, sedationStates) ? sedation : defaultWorkflowState.sedation,
    pain: parseScore(params.get("pain"), 10),
    cpot: parseScore(params.get("cpot"), 8),
    opioid: isOneOf(opioid, opioidChoices) ? opioid : defaultWorkflowState.opioid,
    route: route && /^[a-z0-9-/]+$/i.test(route) ? route : defaultWorkflowState.route,
    flags: parseFlags(params.get("flags")),
  };
}

export function encodeWorkflowState(state: WorkflowState): string {
  const params = new URLSearchParams();

  if (state.age !== defaultWorkflowState.age) params.set("age", state.age);
  if (state.egfr !== defaultWorkflowState.egfr) params.set("egfr", state.egfr);
  if (state.stage !== defaultWorkflowState.stage) params.set("stage", state.stage);
  if (state.sedation !== defaultWorkflowState.sedation) params.set("sedation", state.sedation);
  if (state.pain !== null) params.set("pain", String(state.pain));
  if (state.cpot !== null) params.set("cpot", String(state.cpot));
  if (state.opioid !== defaultWorkflowState.opioid) params.set("opioid", state.opioid);
  if (state.route) params.set("route", state.route);
  if (state.flags.length > 0) params.set("flags", Array.from(new Set(state.flags)).sort().join(","));

  return params.toString();
}

export function mergeWorkflowState(state: WorkflowState, patch: Partial<WorkflowState>): WorkflowState {
  return {
    ...state,
    ...patch,
    flags: patch.flags ? Array.from(new Set(patch.flags)).sort() : state.flags,
  };
}

export function buildUrl(path: string, state: WorkflowState) {
  const query = encodeWorkflowState(state);
  return query ? `${path}?${query}` : path;
}

export function isEgfrAbove30(egfr: EgfrBand) {
  return egfr === "gt40" || egfr === "30to40";
}

export function isEgfrAbove40(egfr: EgfrBand) {
  return egfr === "gt40";
}

export function isEgfrBelow30(egfr: EgfrBand) {
  return egfr === "20to30" || egfr === "lt20";
}

export function painBand(score: number | null) {
  if (score === null) return "not set";
  if (score === 0) return "no pain";
  if (score <= 3) return "mild pain";
  if (score <= 6) return "moderate pain";
  return "severe pain";
}
